import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://dev.api.gioallday.com';

const authHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`
});

const withSupplier = (supplierId: string, extra?: Record<string, string>) => ({
  supplier_id: supplierId,
  ...extra
});

export type VendorRow = {
  id: string;
  business_name?: string | null;
  name?: string | null;
  status?: string;
};

export type VendorSectionRow = {
  id: string;
  supplier_id: string;
  vendor_id: string;
  name: string;
  sort_order: number;
  status: string;
};

export type VendorCategoryRow = {
  id: string;
  section_id: string;
  name: string;
  sort_order: number;
  status: string;
};

export type SupplierCatalogItem = {
  id: string;
  item_name: string | null;
  vendor_category_id: string | null;
  vendor_id?: string | null;
  vendorId?: string | null;
  offerings?: Array<{
    vendorId?: string | null;
    vendor?: { id?: string; business_name?: string | null };
  }>;
};

type Envelope<T> = { success?: boolean; data: T; message?: string };

export const vendorCatalogApi = {
  listVendors: (token: string, supplierId: string) =>
    axios.get<Envelope<VendorRow[]>>(`${API_URL}/api/users/vendorList`, {
      headers: authHeaders(token),
      params: withSupplier(supplierId, { status: 'active' })
    }),

  listSections: (token: string, supplierId: string, vendorId?: string) =>
    axios.get<Envelope<VendorSectionRow[]>>(
      `${API_URL}/api/users/vendor-sections`,
      {
        headers: authHeaders(token),
        params: withSupplier(
          supplierId,
          vendorId ? { vendor_id: vendorId } : undefined
        )
      }
    ),

  createSection: (
    token: string,
    supplierId: string,
    body: { vendor_id: string; name: string }
  ) =>
    axios.post<Envelope<VendorSectionRow>>(
      `${API_URL}/api/users/vendor-sections`,
      body,
      {
        headers: authHeaders(token),
        params: withSupplier(supplierId)
      }
    ),

  updateSection: (
    token: string,
    supplierId: string,
    sectionId: string,
    body: { name?: string }
  ) =>
    axios.patch<Envelope<VendorSectionRow>>(
      `${API_URL}/api/users/vendor-sections/${sectionId}`,
      body,
      {
        headers: authHeaders(token),
        params: withSupplier(supplierId)
      }
    ),

  deleteSection: (token: string, supplierId: string, sectionId: string) =>
    axios.delete(`${API_URL}/api/users/vendor-sections/${sectionId}`, {
      headers: authHeaders(token),
      params: withSupplier(supplierId)
    }),

  listCategories: (token: string, supplierId: string, sectionId: string) =>
    axios.get<Envelope<VendorCategoryRow[]>>(
      `${API_URL}/api/users/vendor-sections/${sectionId}/categories`,
      {
        headers: authHeaders(token),
        params: withSupplier(supplierId)
      }
    ),

  createCategory: (
    token: string,
    supplierId: string,
    sectionId: string,
    body: { name: string }
  ) =>
    axios.post<Envelope<VendorCategoryRow>>(
      `${API_URL}/api/users/vendor-sections/${sectionId}/categories`,
      body,
      {
        headers: authHeaders(token),
        params: withSupplier(supplierId)
      }
    ),

  updateCategory: (
    token: string,
    supplierId: string,
    categoryId: string,
    body: { name?: string }
  ) =>
    axios.patch<Envelope<VendorCategoryRow>>(
      `${API_URL}/api/users/vendor-categories/${categoryId}`,
      body,
      {
        headers: authHeaders(token),
        params: withSupplier(supplierId)
      }
    ),

  deleteCategory: (token: string, supplierId: string, categoryId: string) =>
    axios.delete(`${API_URL}/api/users/vendor-categories/${categoryId}`, {
      headers: authHeaders(token),
      params: withSupplier(supplierId)
    }),

  listItems: (token: string, supplierId: string, search?: string) =>
    axios.get<Envelope<SupplierCatalogItem[]>>(
      `${API_URL}/api/items/supplier_items`,
      {
        headers: authHeaders(token),
        params: {
          supplier_id: supplierId,
          ...(search?.trim() ? { search: search.trim() } : {})
        }
      }
    ),

  assignItemCategory: (
    token: string,
    supplierId: string,
    itemId: string,
    vendorCategoryId: string | null
  ) =>
    axios.patch(
      `${API_URL}/api/users/items/${itemId}/vendor-category`,
      { vendor_category_id: vendorCategoryId },
      {
        headers: authHeaders(token),
        params: withSupplier(supplierId)
      }
    ),

  bulkAssignItemCategory: (
    token: string,
    supplierId: string,
    itemIds: string[],
    vendorCategoryId: string | null
  ) =>
    axios.patch(
      `${API_URL}/api/users/items/vendor-category/bulk`,
      { item_ids: itemIds, vendor_category_id: vendorCategoryId },
      {
        headers: authHeaders(token),
        params: withSupplier(supplierId)
      }
    )
};
